import { execFile } from "node:child_process";
import { promisify } from "node:util";

const execFileAsync = promisify(execFile);
const PACKAGE_NAME = "@mewbleh/nori";

function numeric(version: string): number[] {
  return version.replace(/^v/u, "").split("-")[0]!.split(".").map((part) => Number.parseInt(part, 10) || 0);
}

export function compareVersions(left: string, right: string): number {
  const a = numeric(left);
  const b = numeric(right);
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    if ((a[index] ?? 0) !== (b[index] ?? 0)) return (a[index] ?? 0) < (b[index] ?? 0) ? -1 : 1;
  }
  return 0;
}

export async function updateStatus(installedVersion: string, options: { fetch?: typeof globalThis.fetch; timeoutMs?: number } = {}): Promise<Record<string, unknown>> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 3_000);
  try {
    const response = await (options.fetch ?? globalThis.fetch)(`https://registry.npmjs.org/${encodeURIComponent(PACKAGE_NAME)}/latest`, {
      headers: { accept: "application/json", "user-agent": `nori/${installedVersion}` },
      signal: controller.signal,
    });
    if (!response.ok) return { installedVersion, channel: "stable", available: false, registry: { ok: false, status: response.status, reason: "latest_query_failed" } };
    const body = await response.json() as { version?: unknown };
    const latestVersion = typeof body.version === "string" ? body.version : undefined;
    if (!latestVersion) return { installedVersion, channel: "stable", available: false, registry: { ok: false, reason: "invalid_registry_response" } };
    return {
      installedVersion,
      latestVersion,
      channel: "stable",
      available: compareVersions(installedVersion, latestVersion) < 0,
      registry: { ok: true },
    };
  } catch (error) {
    return { installedVersion, channel: "stable", available: false, registry: { ok: false, reason: error instanceof DOMException && error.name === "AbortError" ? "timeout" : "network_error", message: (error as Error).message } };
  } finally {
    clearTimeout(timeout);
  }
}

export async function runUpdate(installedVersion: string, options: { tag?: string; dryRun?: boolean; timeoutSeconds?: number }): Promise<Record<string, unknown>> {
  const target = options.tag?.trim() || "latest";
  const packageSpec = `${PACKAGE_NAME}@${target}`;
  if (options.dryRun) return { status: "dry-run", installedVersion, target: packageSpec, command: ["npm", "install", "--global", packageSpec] };
  const startedAt = Date.now();
  const { stdout, stderr } = await execFileAsync("npm", ["install", "--global", packageSpec], {
    timeout: Math.max(1, options.timeoutSeconds ?? 1_800) * 1_000,
    maxBuffer: 16 * 1024 * 1024,
    windowsHide: true,
  });
  return { status: "ok", installedVersion, target: packageSpec, durationMs: Date.now() - startedAt, stdout: stdout.trim(), stderr: stderr.trim(), restartRequired: true };
}
